import { useEffect, useRef, useState } from "react";
import { CheckCircle2, AlertTriangle, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { scanSite, sitePaths } from "@/lib/a11yScan";
import TabHeader from "@/components/admin/TabHeader";

const IMPACT = { critical: "bg-red-700", serious: "bg-red-600", moderate: "bg-amber-600", minor: "bg-stone" };

function PageIssues({ page }) {
  return (
    <div data-testid={`a11y-page-${page.path}`} className="bg-paper p-5">
      <p className="font-mono text-xs text-ink">{page.path}</p>
      {page.error && <p className="mt-2 font-body text-sm text-red-700">Couldn’t check this page: {page.error}</p>}
      <ul className="mt-3 space-y-3">
        {page.violations.map((v) => (
          <li key={v.id} className="font-body text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`px-2 py-0.5 font-mono text-[0.6rem] uppercase text-paper ${IMPACT[v.impact] || "bg-stone"}`}>{v.impact || "issue"}</span>
              <span className="text-ink">{v.help}</span>
              <span className="text-muted">· {v.count} place{v.count === 1 ? "" : "s"}</span>
              <a href={v.help_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-stone underline">
                How to fix <ExternalLink className="h-3 w-3" aria-hidden /><span className="sr-only">(opens a new tab)</span>
              </a>
            </div>
            <p className="mt-1 truncate font-mono text-[0.65rem] text-muted">{v.targets.slice(0, 3).join("  |  ")}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function AccessibilityTab() {
  const [data, setData] = useState(null);
  const [progress, setProgress] = useState(null);
  const started = useRef(false);

  const load = () => api.get("/admin/a11y").then((r) => { setData(r.data); return r.data; });

  const run = async (trigger) => {
    if (started.current) return;
    started.current = true;
    try {
      const { data: catalog } = await api.get("/store/catalog");
      const pages = await scanSite(sitePaths(catalog), (i, n, path) => setProgress({ i, n, path }));
      await api.post("/admin/a11y", { trigger, pages });
      toast.success("Accessibility check finished");
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setProgress(null);
      started.current = false;
    }
  };

  useEffect(() => { load().then((d) => d.due && run("monthly")); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const latest = data?.latest;
  const withIssues = latest?.pages.filter((p) => p.violations.length || p.error) || [];

  return (
    <div data-testid="admin-a11y-tab">
      <TabHeader title="Accessibility" note="An automatic check of every page against WCAG 2.2 AA, the standard used for the ADA. It runs here once a month (or whenever you press the button), and lists anything that needs fixing. Automated checks catch most, not all, problems — a person should still review new pages.">
        <button type="button" data-testid="a11y-run-btn" disabled={Boolean(progress)} onClick={() => run("manual")}
          className="inline-flex items-center gap-2 bg-ink px-5 py-3 text-paper disabled:opacity-50">
          {progress && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
          <span className="overline">{progress ? "Checking…" : "Check now"}</span>
        </button>
      </TabHeader>

      {progress && (
        <p role="status" data-testid="a11y-progress" className="mb-6 bg-paper p-4 font-body text-sm text-ink">
          Checking page {progress.i + 1} of {progress.n}: <span className="font-mono">{progress.path}</span> — keep this tab open.
        </p>
      )}

      {data && !latest && !progress && <p className="font-body text-sm text-stone">No check has run yet.</p>}

      {latest && (
        <>
          <div data-testid="a11y-summary" className={`mb-6 flex items-start gap-3 p-5 ${latest.totals.issues ? "bg-[#FBF1EC]" : "bg-[#EEF3EC]"}`}>
            {latest.totals.issues ? <AlertTriangle className="mt-0.5 h-5 w-5 text-red-700" aria-hidden /> : <CheckCircle2 className="mt-0.5 h-5 w-5 text-green-800" aria-hidden />}
            <div className="font-body text-sm text-ink">
              <p data-testid="a11y-summary-text">
                {latest.totals.issues
                  ? `${latest.totals.issues} issue type${latest.totals.issues === 1 ? "" : "s"} found on ${withIssues.length} of ${latest.totals.pages} pages.`
                  : `All ${latest.totals.pages} pages passed the automated WCAG 2.2 AA check.`}
              </p>
              <p className="mt-1 text-xs text-stone">Last checked {new Date(latest.created_at).toLocaleString()} ({latest.trigger}) by {latest.run_by}</p>
            </div>
          </div>
          <div className="space-y-3">{withIssues.map((p) => <PageIssues key={p.path} page={p} />)}</div>
          {data.history.length > 1 && (
            <div className="mt-8">
              <h2 className="overline mb-3 text-muted">History</h2>
              <ul className="divide-y divide-hairline bg-paper font-body text-sm">
                {data.history.map((h) => (
                  <li key={h.id} className="flex justify-between px-5 py-3">
                    <span>{new Date(h.created_at).toLocaleDateString()} · {h.trigger}</span>
                    <span className={h.totals.issues ? "text-red-700" : "text-green-800"}>{h.totals.issues ? `${h.totals.issues} issues` : "Passed"}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
